/**
 * PM schedule display & form helpers (aligns with FacilityKPI_Backend PMPlan embedded fields).
 * Backend: monthly_weekday is 0 = Monday .. 6 = Sunday (Python date.weekday).
 */

export const WEEKDAY_OPTIONS = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

export const MONTHLY_ORDINAL_OPTIONS = [
  { value: 1, label: "First" },
  { value: 2, label: "Second" },
  { value: 3, label: "Third" },
  { value: 4, label: "Fourth" },
  { value: 5, label: "Last" },
];

export function dayOfMonthFromStartDate(startDateStr) {
  if (!startDateStr || typeof startDateStr !== "string") return null;
  const parts = startDateStr.split("-");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[2], 10);
  return Number.isNaN(day) ? null : day;
}

/** @param {object} record — PMPlan or schedule-shaped object from API */
export function deriveMonthlyPattern(record) {
  if (!record || record.cycle_type !== "monthly") return "day_of_month";
  if (
    record.monthly_week_ordinal != null &&
    record.monthly_week_ordinal !== "" &&
    record.monthly_weekday != null &&
    record.monthly_weekday !== ""
  ) {
    return "nth_weekday";
  }
  return "day_of_month";
}

/**
 * Short summary for tables and selects (e.g. "Monthly · day 22", "Every 2 mo · fourth Wednesday").
 * @param {object} record
 */
export function formatPMScheduleRuleSummary(record) {
  if (!record) return "";
  let suffix = "";
  if (record.skip_non_business_days) {
    suffix = record.holiday_country
      ? ` · next biz day (${String(record.holiday_country).toUpperCase()} hol.)`
      : " · next biz day (weekends)";
  }
  const ct = record.cycle_type;
  if (ct === "every_n_days" && record.cycle_value != null) return `${record.cycle_value} days${suffix}`;
  if (ct === "every_n_hours" && record.cycle_value != null) return `${record.cycle_value} hrs${suffix}`;
  if (ct === "annual_regulatory") return `Yearly${suffix}`;
  if (ct === "monthly") {
    const interval = record.monthly_interval || 1;
    const every = interval > 1 ? `Every ${interval} mo` : "Monthly";
    if (record.monthly_day_of_month != null && record.monthly_day_of_month !== "") {
      return `${every} · day ${record.monthly_day_of_month}${suffix}`;
    }
    if (
      record.monthly_week_ordinal != null &&
      record.monthly_week_ordinal !== "" &&
      record.monthly_weekday != null &&
      record.monthly_weekday !== ""
    ) {
      const ord = MONTHLY_ORDINAL_OPTIONS.find((o) => o.value === record.monthly_week_ordinal);
      const wd = WEEKDAY_OPTIONS.find((w) => w.value === record.monthly_weekday);
      const ordLabel = ord ? ord.label.toLowerCase() : String(record.monthly_week_ordinal);
      const wdLabel = wd ? wd.label : "?";
      return `${every} · ${ordLabel} ${wdLabel}${suffix}`;
    }
    return (interval > 1 ? `Every ${interval} mo` : "Monthly") + suffix;
  }
  const base = (ct || "").replace(/_/g, " ") || "";
  return base + suffix;
}

/**
 * Build API payload for PM plan embedded schedule fields (no separate pm_schedules resource).
 * @param {object} values — antd form values including monthly_pattern UI field
 */
export function buildPMPlanSchedulePayload(values) {
  const payload = {
    cycle_type: values.cycle_type,
    start_date: values.start_date,
    end_date: values.end_date || null,
  };

  if (values.cycle_type === "every_n_days" || values.cycle_type === "every_n_hours") {
    payload.cycle_value =
      values.cycle_value != null && values.cycle_value !== ""
        ? Number(values.cycle_value)
        : null;
  } else {
    payload.cycle_value = null;
  }

  if (values.cycle_type === "monthly") {
    payload.monthly_interval =
      values.monthly_interval != null && values.monthly_interval !== ""
        ? Number(values.monthly_interval)
        : 1;
    if (values.monthly_pattern === "day_of_month") {
      payload.monthly_day_of_month =
        values.monthly_day_of_month != null && values.monthly_day_of_month !== ""
          ? Number(values.monthly_day_of_month)
          : null;
      payload.monthly_weekday = null;
      payload.monthly_week_ordinal = null;
    } else {
      payload.monthly_week_ordinal =
        values.monthly_week_ordinal != null && values.monthly_week_ordinal !== ""
          ? Number(values.monthly_week_ordinal)
          : null;
      payload.monthly_weekday =
        values.monthly_weekday != null && values.monthly_weekday !== ""
          ? Number(values.monthly_weekday)
          : null;
      payload.monthly_day_of_month = null;
    }
  } else {
    payload.monthly_interval = 1;
    payload.monthly_day_of_month = null;
    payload.monthly_weekday = null;
    payload.monthly_week_ordinal = null;
  }

  payload.skip_non_business_days = Boolean(values.skip_non_business_days);
  const hc = values.holiday_country;
  if (payload.skip_non_business_days && hc != null && String(hc).trim() !== "") {
    payload.holiday_country = String(hc).trim().toUpperCase();
  } else {
    payload.holiday_country = null;
  }

  return payload;
}
